/**
 * Register filter state and its translation to the calculation-cards query.
 * The rail owns everything that narrows the set; the toolbar owns sort, the
 * models and the minimum deal. The location slice (place + radius, or a map
 * area) is translated by `locationQuery` in location.ts.
 */
import type { CalculationCardQuery, CardSort, MinimumDealRating, PotentialBand, PropertyMarkerQuery } from '../../api/types'
import { humanize } from '../../api/format'
import type { ExperienceSpec, FilterKey } from '../../experiences/registry'
import { DEFAULT_RADIUS_MILES, locationQuery, sanitizeStoredLocation, type LocationArea, type LocationAnchor } from './location'

export interface Filters {
  q: string
  listId: string | null
  status: 'active' | 'archived' | 'merged' | 'all'
  sourceKey: string | null
  visited: boolean | null
  locationAnchor: LocationAnchor | null
  locationRadiusMiles: number
  locationArea: LocationArea | null
  assetClasses: string[]
  dwellingTypes: string[]
  builtForms: string[]
  tenures: string[]
  saleMethods: string[]
  minBedrooms: number | null
  maxBedrooms: number | null
  minPriceK: number | null
  maxPriceK: number | null
  potentialBand: PotentialBand | null
  hasLegalPack: boolean | null
  labelIds: string[]
  minDealRating: MinimumDealRating | null
  sort: CardSort
}

export const DEFAULT_FILTERS: Filters = {
  q: '', listId: null, status: 'active', sourceKey: null, visited: null,
  locationAnchor: null, locationRadiusMiles: DEFAULT_RADIUS_MILES, locationArea: null,
  assetClasses: ['residential'], dwellingTypes: [], builtForms: [], tenures: [], saleMethods: [],
  minBedrooms: null, maxBedrooms: null, minPriceK: null, maxPriceK: null,
  potentialBand: null, hasLegalPack: null, labelIds: [], minDealRating: null, sort: 'updated_desc',
}

export const PAGE_SIZE = 60
export const SESSION_KEY = 'propalpha.next.register.v2'

export const STATUS_OPTIONS = [{ value: 'active', label: 'Active' }, { value: 'archived', label: 'Archived' }, { value: 'merged', label: 'Merged' }, { value: 'all', label: 'All' }]
export const ASSET_CLASS_OPTIONS = ['residential', 'commercial', 'land', 'mixed_use', 'other'].map(v => ({ value: v, label: humanize(v) }))
export const DWELLING_OPTIONS = ['house', 'flat', 'bungalow', 'maisonette', 'park_home'].map(v => ({ value: v, label: humanize(v) }))
export const BUILT_FORM_OPTIONS = ['detached', 'semi_detached', 'mid_terrace', 'end_terrace'].map(v => ({ value: v, label: humanize(v) }))
export const TENURE_OPTIONS = ['freehold', 'leasehold', 'unknown'].map(v => ({ value: v, label: humanize(v) }))
export const SALE_METHOD_OPTIONS = ['auction', 'private_treaty', 'unknown'].map(v => ({ value: v, label: humanize(v) }))
export const VISITED_OPTIONS = [{ value: 'true', label: 'Visited' }, { value: 'false', label: 'Not visited' }]
export const BAND_OPTIONS: Array<{ value: PotentialBand; label: string }> = [
  { value: 'strong', label: 'Strong' }, { value: 'promising', label: 'Promising' }, { value: 'marginal', label: 'Marginal' }, { value: 'weak', label: 'Weak' },
]
export const LEGAL_PACK_OPTIONS = [{ value: 'with', label: 'With a pack' }, { value: 'without', label: 'Without a pack' }]
export const SORT_LABELS: Record<CardSort, string> = {
  updated_desc: 'Recently updated', potential_desc: 'Resale potential', rental_potential_desc: 'Rent potential', price_asc: 'Price: low to high', price_desc: 'Price: high to low', auction_asc: 'Auction date',
}
export const DEAL_OPTIONS: Array<{ value: 'all' | MinimumDealRating; label: string; title?: string }> = [
  { value: 'all', label: 'All', title: 'Include unrated lots' }, { value: 'poor', label: 'Poor+' }, { value: 'average', label: 'Average+' }, { value: 'good', label: 'Good' },
]

const csv = (values: string[]) => (values.length ? values.join(',') : undefined)

/** Translate the rail state into the marker query (no paging), dropping anything the active experience does not offer. */
export function toMarkerQuery(filters: Filters, spec: ExperienceSpec): PropertyMarkerQuery {
  const has = (key: FilterKey) => spec.register.filters.includes(key)
  const sort: CardSort = spec.register.sorts.includes(filters.sort) ? filters.sort : 'updated_desc'
  return {
    q: has('q') && filters.q.trim() ? filters.q.trim() : undefined,
    listId: has('scope') ? filters.listId ?? undefined : undefined,
    status: has('status') ? filters.status : undefined,
    sourceKey: has('source') ? filters.sourceKey ?? undefined : undefined,
    visited: has('visited') ? filters.visited ?? undefined : undefined,
    assetClasses: has('assetClasses') ? csv(filters.assetClasses) : undefined,
    dwellingTypes: has('dwellingTypes') ? csv(filters.dwellingTypes) : undefined,
    builtForms: has('builtForms') ? csv(filters.builtForms) : undefined,
    tenures: has('tenures') ? csv(filters.tenures) : undefined,
    saleMethods: has('saleMethods') ? csv(filters.saleMethods) : undefined,
    minBedrooms: has('beds') ? filters.minBedrooms ?? undefined : undefined,
    maxBedrooms: has('beds') ? filters.maxBedrooms ?? undefined : undefined,
    minPricePence: has('price') && filters.minPriceK !== null ? Math.round(filters.minPriceK * 100_000) : undefined,
    maxPricePence: has('price') && filters.maxPriceK !== null ? Math.round(filters.maxPriceK * 100_000) : undefined,
    potentialBand: has('potentialBand') ? filters.potentialBand ?? undefined : undefined,
    hasLegalPack: has('legalPack') ? filters.hasLegalPack ?? undefined : undefined,
    labelIds: has('labels') ? csv(filters.labelIds) : undefined,
    minDealRating: spec.register.minimumDeal ? filters.minDealRating ?? undefined : undefined,
    ...(has('location') ? locationQuery(filters) : {}),
    sort,
  }
}

export function toQuery(filters: Filters, spec: ExperienceSpec, offset: number): CalculationCardQuery {
  return { ...toMarkerQuery(filters, spec), limit: PAGE_SIZE, offset }
}

const same = (a: string[], b: string[]) => a.length === b.length && a.every(v => b.includes(v))

export function countActive(filters: Filters): number {
  let n = 0
  if (filters.q.trim()) n++
  if (filters.status !== DEFAULT_FILTERS.status) n++
  if (filters.sourceKey) n++
  if (filters.visited !== null) n++
  if (filters.locationAnchor || filters.locationArea) n++
  for (const key of ['assetClasses', 'dwellingTypes', 'builtForms', 'tenures', 'saleMethods', 'labelIds'] as const) if (!same(filters[key], DEFAULT_FILTERS[key])) n++
  if (filters.minBedrooms !== null || filters.maxBedrooms !== null) n++
  if (filters.minPriceK !== null || filters.maxPriceK !== null) n++
  if (filters.potentialBand) n++
  if (filters.hasLegalPack !== null) n++
  return n
}

/** Rebuild filters from any stored object, keeping only known keys and sane location values. */
export function pickKnownFilters(stored: Record<string, unknown>): Filters {
  const clean = sanitizeStoredLocation(stored)
  const merged: Filters = { ...DEFAULT_FILTERS }
  const writable = merged as unknown as Record<string, unknown>
  for (const key of Object.keys(DEFAULT_FILTERS)) if (clean[key] !== undefined) writable[key] = clean[key]
  merged.minDealRating = clean.minDealRating === 'poor' || clean.minDealRating === 'average' || clean.minDealRating === 'good' ? clean.minDealRating : null
  merged.visited = typeof clean.visited === 'boolean' ? clean.visited : null
  merged.potentialBand = ['strong', 'promising', 'marginal', 'weak'].includes(String(clean.potentialBand)) ? clean.potentialBand as PotentialBand : null
  return merged
}

export function readSession(): { filters: Filters; offset: number; view: RegisterView } {
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) ?? 'null') as { filters?: Record<string, unknown>; offset?: number; view?: string } | null
    if (!raw?.filters) return { filters: DEFAULT_FILTERS, offset: 0, view: 'cards' }
    return { filters: pickKnownFilters(raw.filters), offset: typeof raw.offset === 'number' ? raw.offset : 0, view: raw.view === 'table' || raw.view === 'map' ? raw.view : 'cards' }
  } catch { return { filters: DEFAULT_FILTERS, offset: 0, view: 'cards' } }
}

export type RegisterView = 'cards' | 'table' | 'map'

export function writeSession(filters: Filters, offset: number, view: RegisterView): void {
  try { window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ filters, offset, view })) } catch { /* storage may be unavailable */ }
}
